import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Cropper, CropperRef } from 'react-advanced-cropper';
import 'react-advanced-cropper/dist/style.css';

// Global registry to store cropped blobs from all components
const croppedBlobRegistry: Map<string, Blob> = new Map();

interface Props {
    inputName: string;
    aspectRatio?: { width: number; height: number };
    label?: string;
    existingImageUrl?: string | null;
    helpText?: string;
    accept?: string;
}

export default function ImageCropInput({
    inputName,
    aspectRatio = { width: 1, height: 1 },
    label = 'Image',
    existingImageUrl,
    helpText,
    accept = 'image/png,image/jpeg',
}: Props) {
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(existingImageUrl || null);

    const cropperRef = useRef<CropperRef>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const aspectRatioValue = aspectRatio.width / aspectRatio.height;

    // Setup form submission handler (only once per form)
    useEffect(() => {
        if (!containerRef.current) return;

        const form = containerRef.current.closest('form') as HTMLFormElement;
        if (!form) return;

        // Check if handler is already attached
        if ((form as any).__cropSubmitHandler) return;

        const handleSubmit = async (e: Event) => {
            // Check if we have any cropped blobs
            if (croppedBlobRegistry.size === 0) return;

            e.preventDefault();
            e.stopPropagation();

            const formData = new FormData(form);

            // Add all cropped blobs from registry
            croppedBlobRegistry.forEach((blob, name) => {
                formData.delete(name);
                formData.append(name, blob, 'cropped_image.jpg');
                console.log('Adding blob for:', name, 'size:', blob.size);
            });

            try {
                const response = await fetch(form.action, {
                    method: form.method || 'POST',
                    body: formData,
                    headers: {
                        'Accept': 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml',
                    },
                });

                // Clear registry after submission
                croppedBlobRegistry.clear();

                if (response.redirected) {
                    window.location.href = response.url;
                } else if (response.ok) {
                    const html = await response.text();
                    if (html.includes('turbo-stream')) {
                        document.body.insertAdjacentHTML('beforeend', html);
                    } else {
                        window.location.reload();
                    }
                }
            } catch (error) {
                console.error('Form submission error:', error);
            }
        };

        form.addEventListener('submit', handleSubmit);
        (form as any).__cropSubmitHandler = handleSubmit;

        return () => {
            form.removeEventListener('submit', handleSubmit);
            delete (form as any).__cropSubmitHandler;
        };
    }, []);

    // Cleanup blob from registry on unmount
    useEffect(() => {
        return () => {
            croppedBlobRegistry.delete(inputName);
        };
    }, [inputName]);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            setImageSrc(reader.result as string);
            setIsModalOpen(true);
        };
        reader.readAsDataURL(file);
    };

    const handleSave = useCallback(() => {
        const cropper = cropperRef.current;
        if (!cropper) return;

        const canvas = cropper.getCanvas();
        if (!canvas) {
            console.error('No canvas from cropper');
            return;
        }

        // Convert to blob and store in global registry
        canvas.toBlob((blob) => {
            if (!blob) {
                console.error('Failed to create blob');
                return;
            }
            croppedBlobRegistry.set(inputName, blob);
            console.log('Stored cropped blob for:', inputName, 'size:', blob.size, 'registry size:', croppedBlobRegistry.size);
        }, 'image/jpeg', 0.9);

        // Update preview
        setPreviewUrl(canvas.toDataURL('image/jpeg', 0.9));
        setIsModalOpen(false);
        setImageSrc(null);
    }, [inputName]);

    const handleCancel = () => {
        setIsModalOpen(false);
        setImageSrc(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        // Remove from registry if cancelled
        croppedBlobRegistry.delete(inputName);
    };

    return (
        <div ref={containerRef} className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">{label}</label>

            {previewUrl && (
                <div className="mb-2">
                    <img
                        src={previewUrl}
                        alt="Preview"
                        className="rounded-lg shadow-lg max-w-full"
                        style={{ maxHeight: aspectRatioValue >= 1 ? '150px' : '200px' }}
                    />
                </div>
            )}

            <input
                ref={fileInputRef}
                type="file"
                name={inputName}
                accept={accept}
                onChange={handleFileSelect}
                className="m-1 p-2 shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 rounded-md"
            />

            {helpText && <p className="text-xs leading-4 text-gray-500">{helpText}</p>}

            {isModalOpen && imageSrc && (
                <div className="fixed inset-0 z-50 flex bg-black bg-opacity-75 items-center justify-center p-4">
                    <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all sm:max-w-2xl sm:w-full">
                        <div className="px-4 py-5 sm:p-6">
                            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                                Crop {label}
                            </h3>
                            <div style={{ maxHeight: '60vh', overflow: 'hidden' }}>
                                <Cropper
                                    ref={cropperRef}
                                    src={imageSrc}
                                    stencilProps={{
                                        aspectRatio: aspectRatioValue,
                                    }}
                                    className="cropper"
                                    style={{ maxHeight: '50vh' }}
                                />
                            </div>
                        </div>
                        <div className="bg-gray-50 px-4 py-3 sm:px-6 flex flex-row-reverse gap-2">
                            <button
                                type="button"
                                onClick={handleSave}
                                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                            >
                                Apply Crop
                            </button>
                            <button
                                type="button"
                                onClick={handleCancel}
                                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
